import React, { useEffect } from 'react';
import { Trash2, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import '../styles/DataDeletion.css';

const DataDeletionPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="data-deletion-page">
      <section className="deletion-hero">
        <div className="container">
          <Trash2 className="hero-icon" />
          <h1>Eliminación de Datos de Usuario</h1>
          <p className="subtitle">
            <strong>UNILEARN SpA</strong> · RUT <strong>77.291.711-2</strong>
          </p>
        </div>
      </section>

      <div className="deletion-content">
        <div className="container">
          <div className="deletion-intro">
            <AlertCircle className="intro-icon" />
            <div>
              <p>
                En cumplimiento con las políticas de privacidad y protección de datos, 
                UNILEARN te permite solicitar la eliminación completa de tus datos personales 
                asociados a tu cuenta y uso de nuestros servicios.
              </p>
            </div>
          </div>

          <div className="deletion-sections">
            <section className="deletion-section">
              <div className="section-header">
                <h2>Cómo Solicitar la Eliminación de tus Datos</h2>
              </div>
              <div className="section-content">
                <p>
                  Para solicitar la eliminación de tus datos personales de nuestros sistemas, 
                  envía un correo electrónico a:
                </p>
                
                <div className="email-box">
                  <a href="mailto:eliminardato@unilearn.cl" className="email-link">
                    eliminardato@unilearn.cl
                  </a>
                </div>

                <p>
                  En tu solicitud, por favor incluye la siguiente información para que podamos 
                  verificar tu identidad y procesar tu solicitud correctamente:
                </p>

                <ul>
                  <li>Tu nombre completo</li>
                  <li>Correo electrónico asociado a tu cuenta o servicios de UNILEARN</li>
                  <li>Número de teléfono (si lo proporcionaste)</li>
                  <li>Nombre de la institución educativa (si aplica)</li>
                  <li>Descripción breve de los servicios que utilizaste</li>
                </ul>

                <div className="info-box">
                  <AlertCircle className="box-icon" />
                  <div>
                    <p className="box-title">Importante</p>
                    <p>
                      Para proteger tu seguridad y privacidad, necesitamos verificar tu identidad 
                      antes de procesar cualquier solicitud de eliminación de datos. Es posible 
                      que te solicitemos información adicional durante el proceso.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>Qué Datos se Eliminarán</h2>
              </div>
              <div className="section-content">
                <p>
                  Al solicitar la eliminación de tus datos, procederemos a eliminar o anonimizar 
                  la siguiente información:
                </p>

                <div className="data-grid">
                  <div className="data-item">
                    <h3>Datos de Identificación</h3>
                    <ul>
                      <li>Nombre y apellidos</li>
                      <li>RUT u otro identificador</li>
                      <li>Correo electrónico</li>
                      <li>Número de teléfono</li>
                    </ul>
                  </div>

                  <div className="data-item">
                    <h3>Datos Académicos</h3>
                    <ul>
                      <li>Información de matrícula</li>
                      <li>Historial de cursos</li>
                      <li>Certificaciones</li>
                      <li>Registros de progreso</li>
                    </ul>
                  </div>

                  <div className="data-item">
                    <h3>Datos de Comunicación</h3>
                    <ul>
                      <li>Historial de mensajes</li>
                      <li>Comunicaciones por email</li>
                      <li>Registros de WhatsApp</li>
                      <li>Tickets de soporte</li>
                    </ul>
                  </div>

                  <div className="data-item">
                    <h3>Datos Técnicos</h3>
                    <ul>
                      <li>Dirección IP</li>
                      <li>Información del dispositivo</li>
                      <li>Registros de acceso</li>
                      <li>Cookies y preferencias</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>Excepciones y Datos Retenidos</h2>
              </div>
              <div className="section-content">
                <p>
                  En algunos casos, podemos estar legalmente obligados a retener ciertos datos 
                  incluso después de tu solicitud de eliminación:
                </p>

                <ul>
                  <li>
                    <strong>Obligaciones legales y fiscales:</strong> Datos necesarios para cumplir 
                    con leyes tributarias, contables o regulatorias (por ejemplo, facturas y registros 
                    de pago).
                  </li>
                  <li>
                    <strong>Resolución de disputas:</strong> Información necesaria para defender 
                    reclamaciones legales o resolver disputas pendientes.
                  </li>
                  <li>
                    <strong>Seguridad y prevención de fraude:</strong> Registros necesarios para 
                    proteger la seguridad de nuestros sistemas y prevenir actividades fraudulentas.
                  </li>
                  <li>
                    <strong>Datos anonimizados:</strong> Información agregada o anonimizada que no 
                    puede identificarte personalmente y se utiliza para análisis estadísticos.
                  </li>
                </ul>

                <p className="muted">
                  Estos datos se conservarán únicamente durante el tiempo requerido por ley o 
                  hasta que ya no sean necesarios para los fines específicos mencionados.
                </p>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>
                  <Clock className="inline-icon" />
                  Plazos de Procesamiento
                </h2>
              </div>
              <div className="section-content">
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker">1</div>
                    <div className="timeline-content">
                      <h3>Recepción de Solicitud</h3>
                      <p>
                        Recibirás una confirmación automática de que hemos recibido tu solicitud 
                        dentro de las <strong>24-48 horas</strong> siguientes.
                      </p>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-marker">2</div>
                    <div className="timeline-content">
                      <h3>Verificación de Identidad</h3>
                      <p>
                        Verificaremos tu identidad y validaremos tu solicitud. Este proceso puede 
                        tomar <strong>2-5 días hábiles</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-marker">3</div>
                    <div className="timeline-content">
                      <h3>Procesamiento de Eliminación</h3>
                      <p>
                        Una vez verificada tu identidad, procederemos a eliminar tus datos. 
                        Este proceso se completará en un plazo de <strong>30 días</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-marker">
                      <CheckCircle size={20} />
                    </div>
                    <div className="timeline-content">
                      <h3>Confirmación Final</h3>
                      <p>
                        Te enviaremos una confirmación por correo electrónico cuando el proceso 
                        de eliminación se haya completado exitosamente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>Consecuencias de la Eliminación de Datos</h2>
              </div>
              <div className="section-content">
                <div className="warning-box">
                  <AlertCircle className="box-icon" />
                  <div>
                    <p className="box-title">Atención: Esta acción es irreversible</p>
                    <p>
                      Una vez que tus datos sean eliminados, no podremos recuperarlos. 
                      Considera las siguientes consecuencias antes de proceder:
                    </p>
                  </div>
                </div>

                <ul className="consequences-list">
                  <li>Perderás el acceso a cualquier plataforma o servicio de UNILEARN</li>
                  <li>No podrás recuperar certificados, calificaciones o historial académico</li>
                  <li>Se eliminarán todas tus preferencias y configuraciones personalizadas</li>
                  <li>No recibirás más comunicaciones de UNILEARN</li>
                  <li>Si deseas usar nuestros servicios nuevamente, deberás registrarte desde cero</li>
                </ul>

                <p className="muted">
                  Te recomendamos descargar cualquier información importante (certificados, 
                  diplomas, materiales de estudio) antes de solicitar la eliminación de tus datos.
                </p>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>Alternativas a la Eliminación Completa</h2>
              </div>
              <div className="section-content">
                <p>
                  Si no deseas eliminar completamente tus datos, considera estas alternativas:
                </p>

                <div className="alternatives-grid">
                  <div className="alternative-card">
                    <h3>Desactivación de Cuenta</h3>
                    <p>
                      Desactiva temporalmente tu cuenta sin eliminar tus datos. Podrás 
                      reactivarla en cualquier momento.
                    </p>
                  </div>

                  <div className="alternative-card">
                    <h3>Actualización de Datos</h3>
                    <p>
                      Corrige o actualiza información incorrecta en lugar de eliminar todo.
                    </p>
                  </div>

                  <div className="alternative-card">
                    <h3>Control de Comunicaciones</h3>
                    <p>
                      Cancela suscripciones a newsletters o comunicaciones comerciales sin 
                      eliminar tu cuenta.
                    </p>
                  </div>

                  <div className="alternative-card">
                    <h3>Restricción de Uso</h3>
                    <p>
                      Limita el uso de tus datos para ciertos fines específicos.
                    </p>
                  </div>
                </div>

                <p>
                  Para estas alternativas, también puedes escribirnos a: 
                  <a href="mailto:contacto@unilearn.cl"> contacto@unilearn.cl</a>
                </p>
              </div>
            </section>

            <section className="deletion-section">
              <div className="section-header">
                <h2>Contacto y Soporte</h2>
              </div>
              <div className="section-content">
                <p>
                  Si tienes dudas sobre el proceso de eliminación de datos o necesitas asistencia:
                </p>

                <div className="contact-grid">
                  <div className="contact-card">
                    <h3>Eliminación de Datos</h3>
                    <a href="mailto:eliminardato@unilearn.cl">eliminardato@unilearn.cl</a>
                    <p className="muted">Para solicitudes de eliminación</p>
                  </div>

                  <div className="contact-card">
                    <h3>Contacto General</h3>
                    <a href="mailto:contacto@unilearn.cl">contacto@unilearn.cl</a>
                    <p className="muted">Para consultas y soporte</p>
                  </div>

                  <div className="contact-card">
                    <h3>Política de Privacidad</h3>
                    <a href="/politica-privacidad">Ver política completa</a>
                    <p className="muted">Conoce más sobre tus derechos</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionPage;
